import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  UploadCloud, FileText, Trash2, Sparkles, Loader2, CheckCircle2,
  AlertTriangle, Plus, RefreshCw, ArrowRight,
} from "lucide-react";

const PARTY_ROLES = ["Applicant", "Appellant", "Respondent", "Opponent", "Authority", "Other"];
const AUTHORITY_LEVELS = ["Mamlatdar", "Prant", "SDM", "Collector", "District Magistrate", "Other"];
const OUTCOMES = ["Allowed", "Rejected", "Partly Allowed", "Withdrawn", "Other"];
const DOC_CATEGORIES = [
  "Party Submission", "SDM / Prant Order", "Collector Order",
  "SSRD Sample Order", "Government Resolution / GR",
  "Citation / Judgment", "Evidence / Annexure", "Other",
];
const CASE_TYPES = ["Appeal", "Revision", "Review", "Miscellaneous", "Other"];
const LAND_UNITS = ["Acres", "Hectares", "Sq. Mtrs."];

const fmtBytes = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;

/** Per-file OCR may finish before LLM extraction; keep rows in sync with the header until intake completes. */
function intakeFileOcrDisplay(fr, intakeStatus) {
  if (fr.ocr_status === "Failed") {
    return <span className="text-red-700">Failed</span>;
  }
  if (intakeStatus !== "Completed") {
    return <span className="text-primary">Processing</span>;
  }
  if (fr.ocr_status === "Completed") {
    return <span className="text-emerald-700">Completed</span>;
  }
  return <span className="text-muted-foreground">{fr.ocr_status || "Pending"}</span>;
}

function ConfBadge({ value }) {
  if (value === undefined || value === null) return null;
  const v = Number(value);
  const cls = v >= 0.85 ? "bg-emerald-50 text-emerald-800 border-emerald-200"
            : v >= 0.6  ? "bg-amber-50 text-amber-800 border-amber-200"
            :             "bg-red-50 text-red-800 border-red-200";
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono border ${cls}`}>{(v * 100).toFixed(0)}%</span>;
}

export default function CaseIntake() {
  const [phase, setPhase] = useState("upload"); // upload | processing | review
  const [files, setFiles] = useState([]); // {file, id}
  const [dragging, setDragging] = useState(false);
  const [intakeId, setIntakeId] = useState(null);
  const [intake, setIntake] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const addFiles = (fileList) => {
    const newOnes = Array.from(fileList).map((f) => ({
      id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
    }));
    setFiles((prev) => [...prev, ...newOnes]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const runIntake = async () => {
    if (!files.length) return toast.error("Please add at least one file");
    setPhase("processing");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f.file));
    try {
      const { data } = await api.post("/cases/intake", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIntakeId(data.intake_id);
      toast.info(`Intake ${data.intake_id.slice(0, 8)} started — running OCR and extraction`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start intake");
      setPhase("upload");
    }
  };

  // Poll intake status
  useEffect(() => {
    if (!intakeId || phase !== "processing") return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/intake/${intakeId}`);
        setIntake(data);
        if (data.status === "Completed") {
          setProposal(data.proposal || {});
          setPhase("review");
          stop = true;
          toast.success("Extraction complete — please review the details");
        } else if (data.status === "Failed") {
          toast.error(data.error || "Intake failed");
          setPhase("upload");
          stop = true;
        }
      } catch (e) { /* keep polling */ }
      if (!stop) setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; };
  }, [intakeId, phase]);

  const confirm = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/intake/${intakeId}/confirm`, { proposal });
      toast.success(`Case ${data.case_number} created with ${data.parties_created} parties, ${data.properties_created} properties, ${data.lower_orders_created} lower orders`);
      navigate(`/cases/${data.case_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create case");
    } finally {
      setCreating(false);
    }
  };

  // ----- mutators on proposal -----
  const setCM = (k, v) => setProposal((p) => ({ ...p, case_master: { ...(p.case_master || {}), [k]: v } }));
  const setParty = (i, k, v) => setProposal((p) => ({
    ...p, parties: p.parties.map((x, idx) => idx === i ? { ...x, [k]: v } : x),
  }));
  const addParty = () => setProposal((p) => ({
    ...p, parties: [...(p.parties || []), { party_role: "Respondent", party_name: "", party_sequence: (p.parties?.length || 0) + 1 }],
  }));
  const delParty = (i) => setProposal((p) => ({ ...p, parties: p.parties.filter((_, idx) => idx !== i) }));

  const setProp = (i, k, v) => setProposal((p) => ({
    ...p, properties: p.properties.map((x, idx) => idx === i ? { ...x, [k]: v } : x),
  }));
  const addProp = () => setProposal((p) => ({
    ...p, properties: [...(p.properties || []), { district: "", survey_number: "" }],
  }));
  const delProp = (i) => setProposal((p) => ({ ...p, properties: p.properties.filter((_, idx) => idx !== i) }));

  const setLO = (i, k, v) => setProposal((p) => ({
    ...p, lower_orders: p.lower_orders.map((x, idx) => idx === i ? { ...x, [k]: v } : x),
  }));
  const addLO = () => setProposal((p) => ({
    ...p, lower_orders: [...(p.lower_orders || []), { authority_level: "Collector" }],
  }));
  const delLO = (i) => setProposal((p) => ({ ...p, lower_orders: p.lower_orders.filter((_, idx) => idx !== i) }));

  const setCat = (file_index, category) => setProposal((p) => {
    const cats = [...(p.document_categories || [])];
    const ex = cats.findIndex((c) => c.file_index === file_index);
    if (ex >= 0) cats[ex] = { ...cats[ex], category }; else cats.push({ file_index, category, category_confidence: 1 });
    return { ...p, document_categories: cats };
  });

  // ----- render -----
  return (
    <Layout breadcrumbs={["SSRD", "Cases", "Smart Intake"]}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Auto-extract</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="intake-title">Smart Intake</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Drop the case bundle and let Remi8 read every document. Parties,
            land details and lower-authority orders are extracted, sub-orders
            inside a single bundled PDF are split out, and document categories
            are auto-tagged. Review, edit and confirm to create the case.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/cases/new/manual")} data-testid="intake-fallback-manual">
          Create empty case manually
        </Button>
      </div>

      {/* Phase tracker */}
      <div className="flex items-center gap-2 mb-6 text-xs">
        {["upload", "processing", "review"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`rounded-md px-2.5 py-1 border ${phase === s ? "bg-primary text-white border-primary" : "bg-secondary/40 border-border"}`}>
              <span className="font-mono mr-1">0{i + 1}</span> {s.charAt(0).toUpperCase() + s.slice(1)}
            </span>
            {i < 2 && <ArrowRight className="size-3 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {phase === "upload" && (
        <Card>
          <CardContent className="p-6">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`rounded-md border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              data-testid="intake-dropzone"
            >
              <UploadCloud className="size-10 mx-auto text-primary mb-3" />
              <div className="text-base font-semibold">Drop case documents here</div>
              <div className="text-xs text-muted-foreground mt-1">PDF / image · multiple files allowed · bundled orders split automatically</div>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                accept=".pdf,image/*"
                onChange={(e) => e.target.files && addFiles(e.target.files)}
                data-testid="intake-file-input"
              />
            </div>

            {files.length > 0 && (
              <div className="mt-5">
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-2">
                  Queued ({files.length})
                </div>
                <div className="space-y-2" data-testid="intake-file-list">
                  {files.map((f) => (
                    <div key={f.id} className="flex items-center justify-between border border-border rounded-md p-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="size-4 text-muted-foreground shrink-0" />
                        <span className="truncate">{f.file.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">· {fmtBytes(f.file.size)}</span>
                      </div>
                      <button onClick={() => removeFile(f.id)} className="text-muted-foreground hover:text-red-700" data-testid={`intake-remove-${f.id}`}>
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button disabled={!files.length} onClick={runIntake} className="gap-2" data-testid="intake-run-btn">
                <Sparkles className="size-4" /> Run Smart Intake
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {phase === "processing" && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 className="size-5 animate-spin text-primary" />
              <div>
                <div className="font-semibold">Reading documents…</div>
                <div className="text-xs text-muted-foreground">OCR ➜ structured extraction. This usually takes 30–90 seconds.</div>
              </div>
            </div>
            {intake?.files && (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>File</TableHead><TableHead>Pages</TableHead><TableHead>OCR</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {intake.files.map((fr) => (
                    <TableRow key={fr.file_index}>
                      <TableCell className="text-xs">{fr.name}</TableCell>
                      <TableCell className="text-xs">{fr.page_count || "—"}</TableCell>
                      <TableCell className="text-xs" data-testid={`intake-ocr-status-${fr.file_index}`}>
                        {intakeFileOcrDisplay(fr, intake?.status)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {phase === "review" && proposal && (
        <div className="space-y-6">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 p-3 text-xs flex items-center gap-2" data-testid="intake-success-banner">
            <CheckCircle2 className="size-4" />
            <span>Extraction complete. Fields with low confidence are flagged in amber — please verify before saving.</span>
          </div>

          {/* Case master */}
          <Card data-testid="intake-section-master"><CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">A · Case Master</div>
                <h2 className="text-lg font-semibold">Identification</h2>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <FieldEd label="Case Number *" conf={proposal.field_confidence?.case_number}>
                <Input value={proposal.case_master?.case_number || ""} onChange={(e) => setCM("case_number", e.target.value)} data-testid="im-case-number" />
              </FieldEd>
              <FieldEd label="Case Year" conf={proposal.field_confidence?.case_year}>
                <Input value={proposal.case_master?.case_year || ""} onChange={(e) => setCM("case_year", e.target.value)} />
              </FieldEd>
              <FieldEd label="Case Type" conf={proposal.field_confidence?.case_type}>
                <Select value={proposal.case_master?.case_type || "Appeal"} onValueChange={(v) => setCM("case_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CASE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </FieldEd>
              <FieldEd label="District" conf={proposal.field_confidence?.district}>
                <Input value={proposal.case_master?.district || ""} onChange={(e) => setCM("district", e.target.value)} />
              </FieldEd>
              <FieldEd label="Taluka" conf={proposal.field_confidence?.taluka}>
                <Input value={proposal.case_master?.taluka || ""} onChange={(e) => setCM("taluka", e.target.value)} />
              </FieldEd>
              <FieldEd label="Village" conf={proposal.field_confidence?.village}>
                <Input value={proposal.case_master?.village || ""} onChange={(e) => setCM("village", e.target.value)} />
              </FieldEd>
              <FieldEd label="Legal Act"><Input value={proposal.case_master?.legal_act || ""} onChange={(e) => setCM("legal_act", e.target.value)} /></FieldEd>
              <FieldEd label="Legal Section"><Input value={proposal.case_master?.legal_section || ""} onChange={(e) => setCM("legal_section", e.target.value)} /></FieldEd>
              <FieldEd label="Filing Date"><Input type="date" value={proposal.case_master?.filing_date || ""} onChange={(e) => setCM("filing_date", e.target.value)} /></FieldEd>
              <FieldEd label="Subject / Matter Title" wide conf={proposal.field_confidence?.subject_title}>
                <Textarea rows={2} value={proposal.case_master?.subject_title || ""} onChange={(e) => setCM("subject_title", e.target.value)} />
              </FieldEd>
            </div>
          </CardContent></Card>

          {/* Parties */}
          <Card data-testid="intake-section-parties"><CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">B · Parties ({proposal.parties?.length || 0})</div>
                <h2 className="text-lg font-semibold">Parties</h2>
              </div>
              <Button variant="outline" size="sm" onClick={addParty} className="gap-1" data-testid="intake-add-party"><Plus className="size-3.5" /> Add</Button>
            </div>
            {(proposal.parties || []).map((p, i) => (
              <div key={i} className="border border-border rounded-md p-4 relative">
                <button onClick={() => delParty(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-red-700" data-testid={`intake-del-party-${i}`}><Trash2 className="size-4" /></button>
                <div className="grid md:grid-cols-3 gap-3">
                  <FieldEd label="Role">
                    <Select value={p.party_role || "Other"} onValueChange={(v) => setParty(i, "party_role", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PARTY_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldEd>
                  <FieldEd label="Name"><Input className="gujarati" value={p.party_name || ""} onChange={(e) => setParty(i, "party_name", e.target.value)} data-testid={`intake-party-name-${i}`} /></FieldEd>
                  <FieldEd label="Father / Spouse"><Input value={p.father_or_spouse_name || ""} onChange={(e) => setParty(i, "father_or_spouse_name", e.target.value)} /></FieldEd>
                  <FieldEd label="Address" wide><Input value={p.address_line || ""} onChange={(e) => setParty(i, "address_line", e.target.value)} /></FieldEd>
                  <FieldEd label="Village"><Input value={p.village || ""} onChange={(e) => setParty(i, "village", e.target.value)} /></FieldEd>
                  <FieldEd label="Taluka"><Input value={p.taluka || ""} onChange={(e) => setParty(i, "taluka", e.target.value)} /></FieldEd>
                  <FieldEd label="District"><Input value={p.district || ""} onChange={(e) => setParty(i, "district", e.target.value)} /></FieldEd>
                  <FieldEd label="Advocate"><Input value={p.advocate_name || ""} onChange={(e) => setParty(i, "advocate_name", e.target.value)} /></FieldEd>
                </div>
              </div>
            ))}
          </CardContent></Card>

          {/* Properties */}
          <Card data-testid="intake-section-properties"><CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">C · Land / Property ({proposal.properties?.length || 0})</div>
                <h2 className="text-lg font-semibold">Properties</h2>
              </div>
              <Button variant="outline" size="sm" onClick={addProp} className="gap-1" data-testid="intake-add-property"><Plus className="size-3.5" /> Add</Button>
            </div>
            {(proposal.properties || []).map((p, i) => (
              <div key={i} className="border border-border rounded-md p-4 relative">
                <button onClick={() => delProp(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-red-700"><Trash2 className="size-4" /></button>
                <div className="grid md:grid-cols-3 gap-3">
                  <FieldEd label="District"><Input value={p.district || ""} onChange={(e) => setProp(i, "district", e.target.value)} /></FieldEd>
                  <FieldEd label="Taluka"><Input value={p.taluka || ""} onChange={(e) => setProp(i, "taluka", e.target.value)} /></FieldEd>
                  <FieldEd label="Village"><Input value={p.village || ""} onChange={(e) => setProp(i, "village", e.target.value)} /></FieldEd>
                  <FieldEd label="Survey No." conf={proposal.field_confidence?.survey_number}><Input value={p.survey_number || ""} onChange={(e) => setProp(i, "survey_number", e.target.value)} /></FieldEd>
                  <FieldEd label="Block No."><Input value={p.block_number || ""} onChange={(e) => setProp(i, "block_number", e.target.value)} /></FieldEd>
                  <FieldEd label="Land Area"><Input value={p.land_area || ""} onChange={(e) => setProp(i, "land_area", e.target.value)} /></FieldEd>
                  <FieldEd label="Unit">
                    <Select value={p.land_area_unit || "Acres"} onValueChange={(v) => setProp(i, "land_area_unit", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LAND_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldEd>
                  <FieldEd label="Use Type"><Input value={p.land_use_type || ""} onChange={(e) => setProp(i, "land_use_type", e.target.value)} /></FieldEd>
                  <FieldEd label="Disputed Description" wide><Textarea rows={2} className="gujarati" value={p.disputed_property_description || ""} onChange={(e) => setProp(i, "disputed_property_description", e.target.value)} /></FieldEd>
                </div>
              </div>
            ))}
          </CardContent></Card>

          {/* Lower-authority orders */}
          <Card data-testid="intake-section-lower-orders"><CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">D · Lower Authority Orders ({proposal.lower_orders?.length || 0})</div>
                <h2 className="text-lg font-semibold">Lower Orders <span className="text-xs text-muted-foreground font-normal">— sub-orders inside a bundled PDF are split out automatically</span></h2>
              </div>
              <Button variant="outline" size="sm" onClick={addLO} className="gap-1" data-testid="intake-add-lower"><Plus className="size-3.5" /> Add</Button>
            </div>
            {(proposal.lower_orders || []).map((o, i) => (
              <div key={i} className="border border-border rounded-md p-4 relative">
                <button onClick={() => delLO(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-red-700"><Trash2 className="size-4" /></button>
                <div className="grid md:grid-cols-3 gap-3">
                  <FieldEd label="Authority Level">
                    <Select value={o.authority_level || "Other"} onValueChange={(v) => setLO(i, "authority_level", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{AUTHORITY_LEVELS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldEd>
                  <FieldEd label="Authority Name"><Input value={o.authority_name || ""} onChange={(e) => setLO(i, "authority_name", e.target.value)} /></FieldEd>
                  <FieldEd label="Office Location"><Input value={o.office_location || ""} onChange={(e) => setLO(i, "office_location", e.target.value)} /></FieldEd>
                  <FieldEd label="Order Number"><Input value={o.order_number || ""} onChange={(e) => setLO(i, "order_number", e.target.value)} /></FieldEd>
                  <FieldEd label="Order Date"><Input type="date" value={o.order_date || ""} onChange={(e) => setLO(i, "order_date", e.target.value)} /></FieldEd>
                  <FieldEd label="Outcome">
                    <Select value={o.outcome_of_lower_order || "Other"} onValueChange={(v) => setLO(i, "outcome_of_lower_order", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{OUTCOMES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldEd>
                  <FieldEd label="Source File / Pages" wide>
                    <div className="text-xs rounded border border-border p-2 bg-secondary/30">
                      <span className="font-mono">File #{o.source_file_index ?? "?"}</span>
                      {o.page_start && <span> · pages {o.page_start}{o.page_end && o.page_end !== o.page_start ? `–${o.page_end}` : ""}</span>}
                    </div>
                  </FieldEd>
                  <FieldEd label="Challenged Order Summary" wide><Textarea rows={2} className="gujarati" value={o.challenged_order_summary || ""} onChange={(e) => setLO(i, "challenged_order_summary", e.target.value)} /></FieldEd>
                </div>
              </div>
            ))}
          </CardContent></Card>

          {/* Document categories */}
          <Card data-testid="intake-section-categories"><CardContent className="p-0">
            <div className="px-5 py-3 border-b border-border">
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">E · Document Categories</div>
              <div className="text-lg font-semibold">Files</div>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>File</TableHead><TableHead>Pages</TableHead>
                <TableHead>Detected Category</TableHead><TableHead>Confidence</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(intake?.files || []).map((fr) => {
                  const cat = (proposal.document_categories || []).find((c) => c.file_index === fr.file_index);
                  return (
                    <TableRow key={fr.file_index}>
                      <TableCell className="text-xs">{fr.name}</TableCell>
                      <TableCell className="text-xs">{fr.page_count}</TableCell>
                      <TableCell>
                        <Select value={cat?.category || "Other"} onValueChange={(v) => setCat(fr.file_index, v)}>
                          <SelectTrigger className="h-8 max-w-[220px]" data-testid={`intake-cat-${fr.file_index}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><ConfBadge value={cat?.category_confidence} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent></Card>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" disabled={creating} onClick={() => { setPhase("upload"); setProposal(null); setIntake(null); setIntakeId(null); }} className="gap-2" data-testid="intake-restart">
              <RefreshCw className="size-4" /> Start over
            </Button>
            <Button disabled={creating} onClick={confirm} className="gap-2" data-testid="intake-confirm-btn">
              {creating ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
              {creating ? "Creating case…" : "Confirm & Create Case"}
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}

function FieldEd({ label, children, wide = false, conf }) {
  const lc = typeof conf === "number" && conf < 0.6;
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <div className="flex items-center justify-between mb-1">
        <Label className={`text-xs ${lc ? "text-amber-800" : "text-muted-foreground"}`}>
          {label}
          {lc && <AlertTriangle className="inline size-3 ml-1 text-amber-700" />}
        </Label>
        <ConfBadge value={conf} />
      </div>
      {children}
    </div>
  );
}
